import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchCustomerMe, fetchShopSettings } from "../lib/api";

const CART_KEY = "sb_cart";
const CUST_KEY = "sb_customer_token";

const ShopContext = createContext(null);

export const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const readCart = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

export const ShopProvider = ({ children }) => {
  const [cart, setCart] = useState(readCart);
  const [token, setToken] = useState(localStorage.getItem(CUST_KEY) || "");
  const [customer, setCustomer] = useState(null);
  const [settings, setSettings] = useState({ delivery_fee: 19.95, free_shipping_over: null });

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    fetchShopSettings().then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) {
      setCustomer(null);
      return;
    }
    fetchCustomerMe(token)
      .then((r) => setCustomer(r.data))
      .catch(() => {
        localStorage.removeItem(CUST_KEY);
        setToken("");
        setCustomer(null);
      });
  }, [token]);

  const signIn = (newToken, cust) => {
    localStorage.setItem(CUST_KEY, newToken);
    setToken(newToken);
    setCustomer(cust);
  };

  const signOut = () => {
    localStorage.removeItem(CUST_KEY);
    setToken("");
    setCustomer(null);
  };

  const addToCart = (product, quantity = 1, scents = []) => {
    setCart((prev) => {
      const key = `${product.id}|${scents.join(",")}`;
      const idx = prev.findIndex((l) => `${l.product_id}|${l.scents.join(",")}` === key);
      if (idx > -1) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image: product.image,
          billing_interval: product.billing_interval || null,
          quantity,
          scents,
        },
      ];
    });
  };

  const setQuantity = (index, quantity) =>
    setCart((prev) =>
      quantity <= 0
        ? prev.filter((_, i) => i !== index)
        : prev.map((l, i) => (i === index ? { ...l, quantity } : l))
    );

  const removeLine = (index) => setCart((prev) => prev.filter((_, i) => i !== index));
  const clearCart = () => setCart([]);

  const hasSubscription = useMemo(() => cart.some((l) => l.billing_interval), [cart]);
  const mixedBasket = useMemo(
    () => hasSubscription && cart.some((l) => !l.billing_interval),
    [cart, hasSubscription]
  );

  const subtotal = useMemo(
    () => cart.reduce((sum, l) => sum + l.price * l.quantity, 0),
    [cart]
  );
  const deliveryFee = useMemo(() => {
    if (cart.length === 0 || hasSubscription) return 0;
    const over = settings.free_shipping_over;
    if (over && subtotal >= Number(over)) return 0;
    return Number(settings.delivery_fee || 0);
  }, [cart.length, subtotal, settings, hasSubscription]);

  const count = useMemo(() => cart.reduce((n, l) => n + l.quantity, 0), [cart]);

  return (
    <ShopContext.Provider
      value={{
        cart, addToCart, setQuantity, removeLine, clearCart,
        subtotal, deliveryFee, total: subtotal + deliveryFee, count,
        hasSubscription, mixedBasket,
        token, customer, signIn, signOut, settings,
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => useContext(ShopContext);
